import { Component, Inject, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { BrandingV2Service, FeatureFlagService, SubscriptionTiersService } from '@galaxy/partner';
import { PartnerModelApiService } from '@vendasta/partner';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, map, shareReplay, startWith, switchMap, take } from 'rxjs/operators';
import { AppConfig, AppConfigService } from '../app-config.service';
import { ContractStatusesService } from '../billing/contract/status/contract-statuses.service';
import { UnifiedTOSDialogComponent } from '../common/tos-unified/unified-tos-dialog.component';
import { AccessService, Feature, StubAccessService } from '../core/access';
import { getImageSrc } from '../core/images';
import { PartnerService } from '../core/partner.service';
import { TrialStatus } from '../core/partner/partner-trial-status';
import { UnifiedTOSService } from '../core/tos-unified.service';
import { UserDismissalService } from '../core/user-dismissal.service';
import { HighlightsService, SimpleUrlHighlight } from '../highlights';
import { PartnerTrialStoreService } from '../partner-trial';
import { CardConfig, DashboardService } from './dashboard.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { CRMApiService, CRMCompanyApiService, ListCrmObjectsRequest } from '@vendasta/crm';
import { DomainService, SenderType } from '@vendasta/email';
import { StandardIds } from '@galaxy/crm/static';

declare let window: any;
const SHOULD_SHOW_TOS_MODAL = 'ShouldShowTOSModal';
const chmln = window['chmln'];
const CHECKLIST_DISMISSED_KEY = 'DASHBOARD-CHECKLIST-DISMISSED';

interface WidgetUiState {
  isLastTab: boolean;
  isFirstTab: boolean;
  atNestedTab: boolean;
}

const partnerMetricsFeatureFlagId = '2025_pcc_leads_stats';

@Component({
  styleUrls: ['dashboard.component.scss'],
  templateUrl: './dashboard.component.html',
  providers: [ContractStatusesService],
  standalone: false,
})
export class DashboardComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  widgetState = 'inactive';
  widgetSelectedIndex$$: BehaviorSubject<number> = new BehaviorSubject(0);
  widgetSelectedIndex$: Observable<number>;
  nestedSelectedIndex$$: BehaviorSubject<number> = new BehaviorSubject(0);
  nestedSelectedIndex$: Observable<number>;
  WIDGET_INDEX = 'widgetIndex';
  TAB_INDEX = 'tabIndex';
  MAX_WIDGET_INDEX = 5;
  MAX_TAB_INDEX = 1;
  MILLISECOND_FOR_A_WEEK = 604800000;
  highlights$: Observable<SimpleUrlHighlight[]>;
  hasAccessToSalesAndSuccessCenter$: Observable<boolean>;
  hasAccessToProspects$: Observable<boolean>;
  isFree$: Observable<boolean>;
  widgetUiState$: Observable<WidgetUiState>;

  cardConfig$: Observable<CardConfig[]>;
  showCards$: Observable<boolean>;

  revampedDashboardStatCardsClass$: Observable<string>;

  config: AppConfig;

  partnerSuspended$: Observable<boolean>;

  tosDialog: MatDialogRef<UnifiedTOSDialogComponent>;
  trialExpiringSoon$: Observable<boolean>;
  trialExpired$: Observable<boolean>;
  trialDaysRemaining$: Observable<string>;
  trialExists$: Observable<boolean>;
  bannerDismissed: boolean;
  accountExecutiveEmail: string;
  staleBanner$: Observable<boolean>;
  showChecklist$: Observable<boolean>;
  checkListDismissed: WritableSignal<boolean> = signal(false);

  getImageSrc = getImageSrc;

  needsOnboardingUseCaseSelection$: Observable<boolean>;
  isMobile$: Observable<boolean>;

  showCardSection$: Observable<boolean>;

  showPartnerLeadMetrics$: Observable<boolean> = this.partnerId$.pipe(
    switchMap((id) =>
      this.featureFlagService
        .batchGetStatus(id, '', [partnerMetricsFeatureFlagId])
        .pipe(map((resp) => resp[partnerMetricsFeatureFlagId] || false)),
    ),
    catchError((_) => of(false)),
  );

  constructor(
    private router: Router,
    private dashboardService: DashboardService,
    private contractStatusService: ContractStatusesService,
    private partnerService: PartnerService,
    private subscriptionTiersService: SubscriptionTiersService,
    private trialService: PartnerTrialStoreService,
    private userDismissal: UserDismissalService,
    private appConfigService: AppConfigService,
    @Inject(StubAccessService) private access: AccessService,
    public dialog: MatDialog,
    private highlightsService: HighlightsService,
    private tosService: UnifiedTOSService,
    @Inject('PARTNER_ID') public readonly partnerId$: Observable<string>,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly productAnalyticsService: ProductAnalyticsService,
    private readonly brandingService: BrandingV2Service,
    private readonly crmCompanyApiService: CRMCompanyApiService,
    private readonly crmApiService: CRMApiService,
    private readonly partnerModelApiService: PartnerModelApiService,
    private readonly domainService: DomainService,
    private readonly featureFlagService: FeatureFlagService,
  ) {}

  ngOnInit(): void {
    this.config = this.appConfigService.config;
    this.bannerDismissed = this.getCookie('trialExpiring') === 'dismissed';
    this.accountExecutiveEmail = this.config.accountExecutive;
    this.trialExists$ = this.trialService.partnerHasTrial$;
    this.trialExpiringSoon$ = this.trialService.partnerTrial$.pipe(map((t) => t.expiringSoon));
    this.trialExpired$ = this.trialService.partnerTrial$.pipe(map((t) => t.status === TrialStatus.EXPIRED));
    this.trialDaysRemaining$ = this.trialService.partnerTrial$.pipe(
      map((t) => this.formatRemainingTrialDays(t.remainingDays)),
    );
    this.staleBanner$ = combineLatest([
      this.trialService.partnerTrial$,
      this.partnerService.subscriptionTier$,
      this.subscriptionTiersService.subscriptionTierList$,
    ]).pipe(
      map(([trialSubscription, currentSubscription, subscriptionTiers]) => {
        // get the rank of the trial subscription
        if (trialSubscription.subscriptionTierId) {
          const trialSubscriptionTier = subscriptionTiers.find((st) => st.id === trialSubscription.subscriptionTierId);
          return trialSubscriptionTier.rank <= currentSubscription.rank || trialSubscription.expiredOver30DaysAgo;
        }
        return true;
      }),
      startWith(true),
    );
    this.subscriptions.push(
      this.tosService.tosDataLoaded$.subscribe((isReady) => {
        if (isReady && sessionStorage.getItem(SHOULD_SHOW_TOS_MODAL) !== 'no') {
          if (!this.tosService.hasAcceptedTOS && !this.tosService.isClassicSubscription) {
            this.openTOSDialog();
            sessionStorage.setItem(SHOULD_SHOW_TOS_MODAL, 'no');
          }
        }
      }),
    );

    this.userDismissal.checkTutorialWidget();
    this.subscriptions.push(
      this.userDismissal.ShowTutorialWidget.subscribe((showWidget: boolean) => {
        const widgetIndexStr = this.getCookie(this.WIDGET_INDEX);
        this.widgetSelectedIndex$$.next(widgetIndexStr ? +widgetIndexStr : 0);
        const tabIndexStr = this.getCookie(this.TAB_INDEX);
        this.nestedSelectedIndex$$.next(tabIndexStr ? +tabIndexStr : 0);
        this.widgetState = showWidget ? 'active' : 'inactive';
        if (showWidget) {
          this.loadGTM();
        }
      }),
    );

    this.partnerSuspended$ = this.contractStatusService.getStatusForPartner(this.partnerService.partnerId).pipe(
      map((contractStatus) => {
        const key = 'status';
        return contractStatus[key] === 'delinquent';
      }),
    );

    this.cardConfig$ = this.dashboardService.getCardConfig$();
    this.showCards$ = this.cardConfig$.pipe(map((cc) => cc.filter((c) => c.show).length !== 0));
    this.showCardSection$ = combineLatest([this.showPartnerLeadMetrics$, this.showCards$]).pipe(
      map(([showPartnerLeadMetrics, showCards]) => showPartnerLeadMetrics || showCards),
    );

    this.hasAccessToSalesAndSuccessCenter$ = this.access.hasAccessToFeature(Feature.salesAndSuccessCenterAccess);
    this.hasAccessToProspects$ = this.access.hasAccessToFeature(Feature.prospects);
    this.revampedDashboardStatCardsClass$ = this.hasAccessToSalesAndSuccessCenter$.pipe(
      map((hasAccess) => (hasAccess ? 'col-xs-4' : 'col-xs-6')),
      startWith('col-xs-4'),
    );

    this.isFree$ = this.dashboardService.getIsFree();
    this.showChecklist$ = this.dashboardService.showChecklist$;

    this.highlights$ = this.highlightsService.getHighlights();

    this.widgetSelectedIndex$ = this.widgetSelectedIndex$$.asObservable();
    this.nestedSelectedIndex$ = this.nestedSelectedIndex$$.asObservable();
    this.widgetUiState$ = combineLatest([this.widgetSelectedIndex$, this.nestedSelectedIndex$]).pipe(
      map(([widgetSelectedIndex, nestedSelectedIndex]) => ({
        isLastTab: widgetSelectedIndex >= this.MAX_WIDGET_INDEX && nestedSelectedIndex === this.MAX_TAB_INDEX,
        isFirstTab: widgetSelectedIndex === 0,
        atNestedTab: widgetSelectedIndex >= this.MAX_WIDGET_INDEX,
      })),
    );
    this.isMobile$ = this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small])
      .pipe(map((result) => result.matches));

    this.updateChameleonChecklist();

    if (localStorage.getItem(CHECKLIST_DISMISSED_KEY) === 'true') {
      this.checkListDismissed.set(true);
    }
  }

  /**
   * Update the chameleon checklist items.
   * TODO: Remove when done with the chameleon checklist experiment.
   */
  private updateChameleonChecklist(): void {
    // Check if partner has completed the customize branding step
    chmln.identify(this.config.unifiedUserId);
    // TODO: Figure out if we could check if the user has completed the checklist item and skip tracking if they have
    this.trackCustomizeBranding();
    this.trackCRMCompanies();
    this.trackCRMContacts();
    this.trackCustomizeEmailSettings();
    this.trackCustomizeEmailDomain();
    this.trackAIWebchatLeadCapture();
    // TODO: add the rest of the checklist item tracking calls
  }

  /**
   * Track the AI Webchat Lead Capture checklist item
   * If there's at least one lead that's been captured from the AI Webchat, track the checklist item as completed
   */
  private trackAIWebchatLeadCapture(): void {
    const listRequest = <ListCrmObjectsRequest>{
      namespace: this.config.partnerId,
      pagingOptions: {
        pageSize: 1,
      },
      filtersV2: {
        operator: 1,
        filters: [
          {
            fieldId: StandardIds.ContactSourceName,
            operator: 3,
            values: [
              {
                string: 'Web Chat',
              },
            ],
          },
        ],
      },
    };

    this.crmApiService
      .listContacts(listRequest)
      .pipe(take(1), shareReplay(1))
      .subscribe((response) => {
        if (response?.pagingMetadata?.totalResults > 0) {
          this.trackOnboardingChecklistItem('Completed AI Webchat Lead Capture');
        }
      });
  }

  private trackCustomizeEmailDomain(): void {
    this.domainService.listAllDomains(SenderType.SENDER_TYPE_PARTNER, this.config.partnerId).subscribe((resp) => {
      if (resp?.domains?.length >= 1) {
        this.trackOnboardingChecklistItem('Completed Customize Email Domain');
      }
    });
  }

  private trackCustomizeEmailSettings(): void {
    this.partnerModelApiService
      .getPartnerModel({ partnerId: this.config.partnerId })
      .pipe(take(1), shareReplay(1))
      .subscribe((partnerModel) => {
        const partner = partnerModel.partner;
        if (
          partner?.addressLineOne &&
          partner?.city &&
          partner?.countryCode &&
          partner?.stateCode &&
          partner?.postalCode
        ) {
          this.trackOnboardingChecklistItem('Completed Customize Email Settings');
        }
      });
  }

  private trackCRMContacts(): void {
    const listRequest = <ListCrmObjectsRequest>{
      namespace: this.config.partnerId,
      pagingOptions: {
        pageSize: 1,
      },
    };
    this.crmApiService
      .listContacts(listRequest)
      .pipe(take(1), shareReplay(1))
      .subscribe((response) => {
        if (response?.pagingMetadata?.totalResults >= 5) {
          this.trackOnboardingChecklistItem('Created 5 CRM Contacts');
        }
      });
  }

  private trackCRMCompanies(): void {
    const listRequest = <ListCrmObjectsRequest>{
      namespace: this.config.partnerId,
      pagingOptions: {
        pageSize: 1,
      },
    };
    this.crmCompanyApiService
      .listCompanies(listRequest)
      .pipe(take(1), shareReplay(1))
      .subscribe((response) => {
        if (response?.pagingMetadata?.totalResults >= 5) {
          this.trackOnboardingChecklistItem('Created 5 CRM Companies');
        }
      });
  }

  /**
   * Complete the Customize branding checklist item
   * TODO: Remove when done with the chameleon checklist experiment.
   */
  private trackCustomizeBranding(): void {
    this.brandingService
      .getBranding(this.config.partnerId)
      .pipe(take(1), shareReplay(1))
      .subscribe((branding) => {
        if (branding?.faviconUrl && branding?.logoUrl && branding?.shortcutIconUrl) {
          this.trackOnboardingChecklistItem('Completed Customize Branding');
        }
      });
  }

  private trackOnboardingChecklistItem(eventName: string): void {
    chmln.track(eventName);
    const posthogEventName = eventName.replace(/ /g, '-').toLowerCase();
    this.productAnalyticsService.trackEvent(posthogEventName, 'dashboard', 'checklist');
  }

  public openTOSDialog(): void {
    if (this.tosDialog) {
      return;
    }
    this.tosDialog = this.dialog.open(UnifiedTOSDialogComponent, UnifiedTOSDialogComponent.config());
    this.subscriptions.push(this.tosDialog.afterClosed().subscribe(() => (this.tosDialog = null)));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  mailBilling(): void {
    window.open('mailto:billing@vendasta.com');
  }

  loadGTM(): void {
    const page_index = this.widgetSelectedIndex$$.getValue() + this.nestedSelectedIndex$$.getValue();
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'widgetSwitchTab',
      widgetIndex: page_index,
      forward: true,
    });
  }

  getCookie(key: string): string {
    const keyValue = document.cookie.match('(^|;) ?' + key + '=([^;]*)(;|$)');
    return keyValue ? keyValue[2] : null;
  }

  setCookie(key: string, value: string, expiry: number): void {
    const expires: Date = new Date();
    expires.setTime(expires.getTime() + expiry);
    document.cookie = key + '=' + value + ';path=/' + ';expires=' + expires.toUTCString() + ';secure';
  }

  formatRemainingTrialDays(remainingDays: number): string {
    if (remainingDays <= 0) {
      return 'Trial Expired';
    }
    return ' ' + remainingDays + (remainingDays === 1 ? ' day' : ' days');
  }

  hideBanner(): void {
    this.setCookie('trialExpiring', 'dismissed', this.MILLISECOND_FOR_A_WEEK * this.MILLISECOND_FOR_A_WEEK);
    this.bannerDismissed = true;
  }

  upgradeSubscription(): void {
    this.goToLink('/upgrade');
  }

  goToLink(path: string): void {
    if (path.indexOf('http') >= 0) {
      window.open(path, '_blank');
    } else {
      this.router.navigateByUrl(path);
    }
  }

  manageTeamMembers(): void {
    this.router.navigateByUrl('/my-team');
  }

  dismissChecklist(): void {
    this.checkListDismissed.set(true);
    localStorage.setItem(CHECKLIST_DISMISSED_KEY, 'true');
  }

  trackEvent(name): void {
    this.productAnalyticsService.trackEvent(name, 'dashboard', 'click');
  }
}
